import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  Table,
  Button,
  SpaceBetween,
  Modal,
  FormField,
  Input,
  Box,
  Flashbar,
  Spinner,
} from '@amzn/awsui-components-react';
import { useGetSessionsQuery, useCreateSessionMutation } from 'src/store/apis/uatApi';
import useNotification from 'src/hooks/useNotification';
import { usePermissions } from 'src/hooks/usePermissions';
import {
  SelectedOption,
  LLMTypeSelect,
  PhaseSelect,
  ToolSelectionTable,
  useToolsByPhase,
  llmTypeOptions,
} from 'src/components/common/ExecutionScopeFields';

const UatSessions: FC = () => {
  const navigate = useNavigate();
  const { addNotification, notificationItems } = useNotification();
  const { canAccessUat } = usePermissions();
  const { data: sessions, isLoading } = useGetSessionsQuery();
  const [createSession, { isLoading: isCreating }] = useCreateSessionMutation();
  const { toolsByPhase, isLoading: isLoadingTools } = useToolsByPhase();

  const [showModal, setShowModal] = useState(false);
  const [sessionName, setSessionName] = useState('');
  const [selectedLLMType, setSelectedLLMType] = useState<SelectedOption | null>(llmTypeOptions[0]);
  const [selectedPhase, setSelectedPhase] = useState<SelectedOption | null>(null);
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);

  const resetForm = () => {
    setSessionName('');
    setSelectedLLMType(llmTypeOptions[0]);
    setSelectedPhase(null);
    setSelectedToolIds([]);
  };

  const handlePhaseChange = (option: SelectedOption) => {
    setSelectedPhase(option);
    setSelectedToolIds([]);
  };

  const handleCreate = async () => {
    if (!selectedLLMType || !selectedPhase) return;

    // Build toolVersionRevision from selected tools
    const phase = selectedPhase.value;
    const tools = toolsByPhase?.[phase] ?? [];
    const toolVersionRevision: Record<string, { version: string; revision: number; status: string | null }> = {};
    selectedToolIds.forEach((id) => {
      const t = tools.find((tool) => tool.toolId === id);
      if (t) toolVersionRevision[id] = { version: t.version, revision: t.revision, status: t.status ?? null };
    });

    try {
      const result = await createSession({
        sessionName,
        executionScope: {
          clientLLMType: selectedLLMType.value,
          phase,
          enabledTools: selectedToolIds,
          toolVersionRevision,
        },
      }).unwrap();
      setShowModal(false);
      resetForm();
      navigate(`/uat/sessions/${result.sessionId}`);
    } catch {
      addNotification('error', 'create-error', 'Failed to create session.');
    }
  };

  const isFormValid = sessionName.trim() && selectedLLMType && selectedPhase && selectedToolIds.length > 0;

  return (
    <ContentLayout header={<Header variant="h1">UAT Sessions</Header>}>
      <SpaceBetween size="l">
        <Flashbar items={notificationItems} />
        {isLoading ? (
          <Spinner size="large" />
        ) : (
          <Table
            header={
              <Header
                variant="h2"
                actions={canAccessUat && <Button variant="primary" onClick={() => setShowModal(true)}>Create Session</Button>}
              >
                Sessions
              </Header>
            }
            items={sessions ?? []}
            empty={<Box textAlign="center" padding="l">No sessions yet.</Box>}
            columnDefinitions={[
              {
                id: 'sessionName',
                header: 'Session Name',
                cell: (item) => (
                  <Button variant="link" onClick={() => navigate(`/uat/sessions/${item.sessionId}`)}>
                    {item.sessionName}
                  </Button>
                ),
              },
              { id: 'createdBy', header: 'Created By', cell: (item) => item.createdBy },
              { id: 'createdTs', header: 'Created', cell: (item) => new Date(item.createdTs).toLocaleString() },
              { id: 'participants', header: 'Participants', cell: (item) => item.participants.join(', ') },
              { id: 'totalQueries', header: 'Queries', cell: (item) => item.totalQueries },
              { id: 'totalFeedbacks', header: 'Feedbacks', cell: (item) => item.totalFeedbacks },
              {
                id: 'actions',
                header: 'Actions',
                cell: (item) => (
                  <Button variant="link" onClick={() => navigate(`/uat/sessions/${item.sessionId}/review`)}>
                    Review
                  </Button>
                ),
              },
            ]}
          />
        )}

        <Modal
          visible={showModal}
          onDismiss={() => setShowModal(false)}
          header="Create UAT Session"
          size="large"
          footer={
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button onClick={() => setShowModal(false)}>Cancel</Button>
                <Button variant="primary" onClick={handleCreate} loading={isCreating} disabled={!isFormValid}>
                  Create
                </Button>
              </SpaceBetween>
            </Box>
          }
        >
          <SpaceBetween size="m">
            <FormField label="Session Name">
              <Input value={sessionName} onChange={({ detail }) => setSessionName(detail.value)} />
            </FormField>
            <LLMTypeSelect
              selectedOption={selectedLLMType}
              onChange={setSelectedLLMType}
            />
            <PhaseSelect
              selectedOption={selectedPhase}
              onChange={handlePhaseChange}
            />
            <ToolSelectionTable
              phase={selectedPhase?.value ?? null}
              toolsByPhase={toolsByPhase}
              isLoading={isLoadingTools}
              selectedToolIds={selectedToolIds}
              onSelectionChange={setSelectedToolIds}
            />
          </SpaceBetween>
        </Modal>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default UatSessions;
